import React from 'react';
import type { FieldProps } from '../types';

/**
 * QuestionnaireGrid Component
 *
 * Renders a grid of questions with radio/checkbox/yes-no controls.
 */
export const QuestionnaireGrid: React.FC<FieldProps> = ({
  element,
  value,
  onChange,
  onBlur,
  error,
  disabled,
  theme,
}) => {
  const properties = element.properties;
  const rows = properties?.gridRows || [];
  const columns = properties?.gridColumns || [];
  const controlType = properties?.controlType || 'radio';

  // Value is an object mapping row IDs to selected column values
  const gridValues = typeof value === 'object' && value !== null ? value : {};

  const handleCellChange = (rowId: string, columnValue: string) => {
    if (controlType === 'checkbox') {
      // For checkboxes, toggle the value in an array
      const rowValues = Array.isArray(gridValues[rowId]) ? gridValues[rowId] : [];
      const newRowValues = rowValues.includes(columnValue)
        ? rowValues.filter((v: string) => v !== columnValue)
        : [...rowValues, columnValue];

      onChange({
        ...gridValues,
        [rowId]: newRowValues,
      });
    } else {
      // For radio/yes-no, set single value
      onChange({
        ...gridValues,
        [rowId]: columnValue,
      });
    }
  };

  const isCellSelected = (rowId: string, columnValue: string): boolean => {
    if (controlType === 'checkbox') {
      const rowValues = gridValues[rowId];
      return Array.isArray(rowValues) && rowValues.includes(columnValue);
    } else {
      return gridValues[rowId] === columnValue;
    }
  };

  return (
    <div className="mediform-field mediform-grid-field">
      {properties?.heading && (
        <div className="mediform-field-label mediform-grid-heading">
          {properties.heading}
          {properties.required && <span className="mediform-required"> *</span>}
        </div>
      )}

      <div className="mediform-grid-container">
        <table className="mediform-grid-table">
          <thead>
            <tr>
              <th className="mediform-grid-question-header"></th>
              {columns.map((column) => (
                <th
                  key={column.id}
                  className="mediform-grid-column-header"
                  style={{ width: column.width }}
                >
                  {column.header}
                  {column.score !== undefined && (
                    <span className="mediform-grid-score"> ({column.score})</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="mediform-grid-row">
                <td className="mediform-grid-question-cell">
                  {row.question}
                </td>
                {columns.map((column) => {
                  const cellId = `${element.id}-${row.id}-${column.value}`;
                  const isSelected = isCellSelected(row.id, column.value);

                  return (
                    <td key={column.id} className="mediform-grid-control-cell">
                      {controlType === 'yes-no' ? (
                        // Yes/No: Only show radio if this column is 'yes' or 'no'
                        (column.value === 'yes' || column.value === 'no') && (
                          <input
                            type="radio"
                            id={cellId}
                            name={`${element.id}-${row.id}`}
                            value={column.value}
                            checked={isSelected}
                            onChange={() => handleCellChange(row.id, column.value)}
                            onBlur={onBlur}
                            disabled={disabled}
                            className="mediform-grid-input"
                            aria-label={`${row.question} - ${column.header}`}
                          />
                        )
                      ) : (
                        <input
                          type={controlType === 'checkbox' ? 'checkbox' : 'radio'}
                          id={cellId}
                          name={`${element.id}-${row.id}`}
                          value={column.value}
                          checked={isSelected}
                          onChange={() => handleCellChange(row.id, column.value)}
                          onBlur={onBlur}
                          disabled={disabled}
                          className="mediform-grid-input"
                          aria-label={`${row.question} - ${column.header}`}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {error && (
        <div className="mediform-field-error" style={{ color: theme?.colors?.error }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default QuestionnaireGrid;
